import { metrics, registry } from '@geo-admin/shared/contract/api/index.ts';
import type { RouteResponse } from '@geo-admin/shared/contract/route.ts';
import { Link, useParams } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { DataTable } from '../components/data-table.tsx';
import { EngineTabs, engineLabel, engineSearch, useEngine } from '../components/EngineTabs.tsx';
import { RateCell } from '../components/RateCell.tsx';
import { RateTrend } from '../components/RateTrend.tsx';
import { SampleTable } from '../components/SampleTable.tsx';
import { Badge, Card, CardBody, CardHeader } from '../components/ui/card.tsx';
import { Select } from '../components/ui/form.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi } from '../lib/api.ts';
import { asOfText, onboardingText } from '../lib/format.ts';
import { PageTitle } from '../components/PageTitle.tsx';
import { useMe } from '../lib/me.ts';
import { PATHS } from '../lib/paths.ts';

type MetricsResponse = RouteResponse<typeof metrics.queryMetrics>;

export function QueryDetailPage() {
  // ★ 이제 **운영자 영역(/admin) 전용**이다 — 고객 영역에는 질문 상세가 없다 (260921-client-report-chart-only).
  //   strict:false 는 그대로 둔다: 라우트 id 를 고정하지 않는 편이 다시 공용이 돼도 안전하다
  const { id } = useParams({ strict: false }) as { id: string };
  const me = useMe();
  const isAdmin = me.data?.role === 'admin';
  const [days, setDays] = useState(14);
  // 「ChatGPT | Gemini」 — 운영자 영역 전용 화면이라 탭을 그대로 보낸다 (261104-gemini-concurrent-lane)
  const engine = useEngine();
  const q = useApi(registry.getQuery, { params: { id } });
  const m = useApi(metrics.queryMetrics, { params: { id }, query: { days, engine } });

  if (q.isLoading || m.isLoading) return <Loading />;
  if (q.error || m.error) return <ErrorBox error={q.error ?? m.error} />;
  const query = q.data!.query;
  const data = m.data!;
  const h = data.headline;

  const domainCols: ColumnDef<MetricsResponse['citationDomains'][number], unknown>[] = [
    {
      header: '인용 도메인',
      accessorKey: 'host',
      cell: ({ row }) => (
        <span className="flex items-center gap-1.5">
          {row.original.host}
          {row.original.siteUrlPrefix ? <Badge tone="green">우리 사이트</Badge> : null}
        </span>
      ),
    },
    {
      header: '인용된 샘플 비율',
      id: 'share',
      accessorFn: (r) => r.samples,
      cell: ({ row }) => <RateCell rate={row.original.share} plain />,
    },
  ];

  return (
    <>
      <PageTitle
        title={query.query}
        description={`${asOfText(data.asOf)} · ${query.brandName} · 목표 브랜드명 ${query.brandNames.join(' · ')} · 하루 ${query.samplesPerDay}회${query.deleted ? ' · 삭제됨' : ''}${onboardingText(h.onboarding)}`}
        actions={
          <span className="flex items-center gap-3">
            <EngineTabs />
            <Select className="w-28" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {[7, 14, 30, 90].map((d) => (
                <option key={d} value={d}>
                  {d}일
                </option>
              ))}
            </Select>
          </span>
        }
      />
      {engine !== 'chatgpt-web' ? (
        <p className="text-sm text-slate-500">
          {engineLabel(engine)} 지표입니다 — 운영자에게만 보이고 고객 리포트에는 실리지 않습니다. {engineLabel(engine)}{' '}
          답변에는 장소 카드가 없습니다.
        </p>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {(
          [
            ['브랜드 언급률', h.mention, h.raw?.mention ?? null],
            ['답변 본문', h.mentionInAnswer, h.raw?.mentionInAnswer ?? null],
            ['장소 카드', h.mentionInPlaceCard, h.raw?.mentionInPlaceCard ?? null],
            ['검색 유발률', h.searchTriggered, h.raw?.searchTriggered ?? null],
            ...(isAdmin ? [['우리 사이트 인용률', h.ourSite, null] as const] : []),
          ] as const
        ).map(([label, rate, raw]) => (
          <Card key={label}>
            <CardBody>
              <p className="text-xs text-slate-500">{label}</p>
              {/* 실측이 메인, 온보딩 표시값은 아래 작게 · k/n·95% CI 는 싣지 않는다(운영자 결정 2026-11-03) */}
              <RateCell rate={rate} emphasize raw={raw} plain />
            </CardBody>
          </Card>
        ))}
      </div>
      <Card>
        <CardHeader
          title="일별 브랜드 언급률"
          description="실선 = 실측 · 점선 = 온보딩 표시값 · 빈 점 = 표본 10 미만 · 세로선 = 문구(주황)·측정기(회색) 변경"
        />
        <CardBody>
          <RateTrend daily={data.daily} markers={data.markers} />
        </CardBody>
      </Card>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="인용 도메인" description="적격 샘플 중 그 도메인이 한 번이라도 인용된 비율입니다" />
          <DataTable columns={domainCols} data={data.citationDomains} empty="인용이 없습니다" />
        </Card>
        <Card>
          <CardHeader
            title="최근 샘플"
            actions={
              isAdmin ? (
                <span className="flex items-center gap-3 text-sm">
                  <Link to="/admin/queries" className="text-slate-500 hover:underline">
                    질문 관리
                  </Link>
                  <Link
                    to={PATHS.admin.querySamples}
                    params={{ id }}
                    search={engineSearch(engine)}
                    className="font-medium text-slate-700 hover:underline"
                  >
                    더보기 →
                  </Link>
                </span>
              ) : null
            }
          />
          <SampleTable samples={data.recentSamples} />
        </Card>
      </div>
    </>
  );
}
